import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Shuffle, Zap, PlusCircle, ListChecks, Minus, Plus } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import Avatar from '../ui/avatar-initials';
import { Switch } from '../ui/switch';
import { firstName } from '../../lib/play';
import PlanView from './PlanView';
import QuickMatchForm from './QuickMatchForm';
import { MatchLine } from './RecentMatches';
import CardActions from './CardActions';
import AvoidPairsEditor from './AvoidPairsEditor';
import SquadEditor, { suggestSquads } from './SquadEditor';
import PairsEditor, { suggestPairs } from './PairsEditor';

const FORMATS = [
  { id: 'mixer', label: 'Open shuffle', blurb: "Partners shuffle every round, balanced by rating. Rest rotates fairly (max 2 back-to-back) and recent partners are spread out." },
  { id: 'americano', label: 'Americano', blurb: 'Partners shuffle every round, but every point you score counts to your own tally — win or lose. One ranked table decides the morning.' },
  { id: 'mexicano', label: 'Mexicano', blurb: 'Like Americano, except each round is drawn from the live table — the closest-ranked four share a court as 1+4 vs 2+3, so games stay tight. One round at a time.' },
  { id: 'pairs', label: 'Fixed pairs', blurb: 'Pairs stay together all morning and every pair plays every other pair once. Ranked as teams.' },
  { id: 'king', label: 'King of Court', blurb: 'Winners climb, losers drop. Court 1 is the throne. On a single court the winners stay on and the next two step up.' },
  { id: 'knockout', label: 'Knockout', blurb: 'Straight bracket for fixed pairs — seeded by rating, top seeds get the byes, lose once and you are out. Each round unlocks when the last one is scored.' },
  { id: 'groups_knockout', label: 'Groups + KO', blurb: 'Pairs play a round robin inside their group, then the top finishers go through to a knockout bracket. A proper tournament day.' },
  { id: 'squad', label: 'Squad tie', blurb: 'Two fixed squads — Red vs Blue. Each round a fresh pair from each squad takes the court and the session ends with a squad score.' },
];

const PROGRESSIVE = ['mexicano', 'king', 'knockout', 'groups_knockout'];
const PAIR_FORMATS = ['pairs', 'knockout', 'groups_knockout'];

export default function CourtModeSheet({ open, sessionId, group, user, onClose }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [courts, setCourts] = useState(1);
  const [rounds, setRounds] = useState(4);
  const [view, setView] = useState('players');
  const [busy, setBusy] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [noRepeat, setNoRepeat] = useState(false);
  const [format, setFormat] = useState('mixer');
  const [squads, setSquads] = useState([[], []]);
  const [pairs, setPairs] = useState([]);
  const [picking, setPicking] = useState(null);
  const [pointsToWin, setPointsToWin] = useState(11);
  const [winBonus, setWinBonus] = useState(2);
  const [rated, setRated] = useState(true);
  const [numGroups, setNumGroups] = useState(2);
  const [advance, setAdvance] = useState(2);
  const [fee, setFee] = useState('');
  const mode = format === 'squad' ? 'squad' : 'open';

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const load = useCallback(async () => {
    if (!sessionId) return;
    try {
      const r = await api.get(`/sessions/${sessionId}/play`);
      setData(r.data);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open Court Mode');
      onCloseRef.current?.();
    }
  }, [sessionId]);

  useEffect(() => {
    if (!open) return;
    setData(null);
    setView('players');
    load().then((d) => {
      if (!d) return;
      setCourts(d.session.num_courts || 1);
      setRounds(d.default_rounds || 4);
      setPointsToWin(d.plan?.points_to_win || d.settings?.points_to_win || 11);
      if (d.plan?.rounds?.length) {
        setSelected(d.plan.player_ids || []);
        setNoRepeat(!!d.plan.no_repeat);
        setFormat(d.plan.format || (d.plan.mode === 'squad' ? 'squad' : 'mixer'));
        setWinBonus(d.plan.win_bonus ?? 2);
        setRated(d.plan.rated !== false);
        if (d.plan.pairs) setPairs(d.plan.pairs);
        if (d.plan.squads) setSquads([d.plan.squads.a || [], d.plan.squads.b || []]);
        setView('plan');
      } else {
        setSelected(d.candidates.map((c) => c.id));
        setShowMore(d.candidates.length === 0);
      }
    });
  }, [open, load]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const ratingRef = useRef({});
  const dataRef = useRef(null);
  dataRef.current = data;

  useEffect(() => {
    if (mode !== 'squad') return;
    setSquads(([a, b]) => {
      const sel = new Set(selected);
      const na = a.filter((id) => sel.has(id));
      const nb = b.filter((id) => sel.has(id));
      if (!na.length && !nb.length) return suggestSquads(selected, ratingRef.current);
      selected.filter((id) => !na.includes(id) && !nb.includes(id))
        .forEach((id) => (na.length <= nb.length ? na : nb).push(id));
      return [na, nb];
    });
  }, [mode, selected]);

  // iter313/315 — pair-based formats share the same pair editor.
  useEffect(() => {
    if (!PAIR_FORMATS.includes(format)) return;
    setPicking(null);
    setPairs((prev) => {
      const sel = new Set(selected);
      const kept = prev.filter((p) => p.every((id) => sel.has(id)));
      if (!kept.length) return suggestPairs(selected, ratingRef.current);
      return kept;
    });
  }, [format, selected]);

  // Prefill the fee with whatever this format cost last time (tournament days
  // often cost more than a normal morning).
  useEffect(() => {
    const remembered = dataRef.current?.format_fees?.[format];
    const current = dataRef.current?.session_fee;
    setFee(remembered != null ? String(remembered) : (current != null ? String(current) : ''));
  }, [format]);

  const paired = new Set(pairs.flat());
  const unpaired = selected.filter((id) => !paired.has(id));

  const pickForPair = (id) => {
    if (picking === id) return setPicking(null);
    if (!picking) return setPicking(id);
    setPairs((p) => [...p, [picking, id]]);
    setPicking(null);
  };

  // iter311 — 3v3 (3 per squad) is a best-of-3 tie: AB v DE, BC v EF, AC v DF.
  // Auto-set the rounds to 3 the moment the squads become 3-and-3; the stepper
  // still lets the scorer override.
  const is3v3 = mode === 'squad' && squads[0].length === 3 && squads[1].length === 3;
  useEffect(() => {
    if (is3v3) setRounds(3);
  }, [is3v3]);

  const shuffle = async () => {
    if (selected.length < 4) return toast.error('Pick at least 4 players');
    if (format === 'squad' && (squads[0].length < 2 || squads[1].length < 2)) return toast.error('Each squad needs at least 2 players');
    if (PAIR_FORMATS.includes(format) && pairs.length < 2) return toast.error('Need at least 2 pairs for this format');
    if (format === 'groups_knockout' && pairs.length < numGroups * 2) {
      return toast.error(`Need at least ${numGroups * 2} pairs for ${numGroups} groups`);
    }
    setBusy(true);
    try {
      await api.post(`/sessions/${sessionId}/shuffle`, {
        player_ids: format === 'squad' ? [...squads[0], ...squads[1]] : selected,
        rounds, num_courts: courts, no_repeat_partners: noRepeat,
        mode, format, squad_a: squads[0], squad_b: squads[1],
        pairs: PAIR_FORMATS.includes(format) ? pairs : null,
        points_to_win: pointsToWin,
        win_bonus: format === 'americano' ? winBonus : 0,
        rated,
        num_groups: numGroups,
        advance_per_group: advance,
        session_fee: fee === '' ? null : Number(fee),
      });
      await load();
      setView('plan');
      toast.success({
        squad: 'Squad tie ready — Red vs Blue',
        pairs: 'Round robin ready — every pair plays every pair',
        americano: `Americano ready — games to ${pointsToWin}, every point counts`,
        mexicano: 'Mexicano started — round 1 is on, the next comes from the table',
        king: 'King of the Court started — court 1 is the throne',
        knockout: 'Bracket drawn — win or go home',
        groups_knockout: 'Group stage drawn — knockout unlocks when the groups finish',
      }[format] || 'Teams shuffled — balanced & partner-rotated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Shuffle failed');
    } finally {
      setBusy(false);
    }
  };

  const onLogged = (events) => {
    (events || []).forEach((ev) => toast(ev.text, { duration: 6000 }));
    load();
    window.dispatchEvent(new Event('play:changed'));
  };

  const ratingOf = {};
  (data?.candidates || []).concat(data?.members || []).forEach((p) => { ratingOf[p.id] = p; });
  ratingRef.current = ratingOf;
  const extraMembers = (data?.members || []).filter((m) => !(data?.candidates || []).some((c) => c.id === m.id));

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl h-[94vh] overflow-y-auto" data-testid="court-mode-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-xl inline-flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" /> Court Mode
          </SheetTitle>
          <SheetDescription>
            {data ? `${data.session.title} · ${data.session.date} · ${data.session.num_courts} court${data.session.num_courts > 1 ? 's' : ''} · ${data.session.duration_minutes} min` : 'Loading…'}
            {data?.season ? ` · ${data.season.name}` : ''}
          </SheetDescription>
        </SheetHeader>

        {!data ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-10" /> : (
          <div className="mt-3 pb-6 space-y-4">
            <div className="grid grid-cols-3 gap-1 rounded-xl bg-muted p-1" data-testid="court-mode-tabs">
              {[['players', 'Players', Shuffle], ['plan', 'Schedule', ListChecks], ['quick', 'Quick log', PlusCircle]].map(([k, l, Icon]) => (
                <button key={k} onClick={() => setView(k)} data-testid={`court-mode-tab-${k}`}
                  className={`h-9 rounded-lg text-xs font-bold inline-flex items-center justify-center gap-1 transition-colors ${view === k ? 'bg-background shadow' : 'text-muted-foreground'}`}>
                  <Icon className="w-3.5 h-3.5" /> {l}
                </button>
              ))}
            </div>

            {view === 'players' && (
              <div className="space-y-4" data-testid="court-mode-players">
                <div className="grid grid-cols-3 gap-1 rounded-xl bg-muted p-1" data-testid="play-mode-switch">
                  {FORMATS.map((f) => (
                    <button key={f.id} onClick={() => setFormat(f.id)} data-testid={`play-mode-${f.id}`}
                      className={`h-8 rounded-lg text-xs font-bold transition-colors px-0.5 truncate ${format === f.id ? 'bg-background shadow' : 'text-muted-foreground'}`}>
                      {f.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {format === 'mixer'
                    ? "Who's on court? Tap to toggle. Guests play unrated. "
                    : ''}
                  {FORMATS.find((f) => f.id === format)?.blurb}
                </p>
                <div className="flex flex-wrap gap-2" data-testid="player-chips">
                  {data.candidates.map((c) => <PlayerChip key={c.id} p={c} on={selected.includes(c.id)} onClick={() => toggle(c.id)} />)}
                  {showMore && extraMembers.map((c) => <PlayerChip key={c.id} p={c} on={selected.includes(c.id)} onClick={() => toggle(c.id)} />)}
                </div>
                {extraMembers.length > 0 && (
                  <button className="text-xs font-semibold text-primary" onClick={() => setShowMore((v) => !v)} data-testid="toggle-more-players">
                    {showMore ? 'Hide other members' : `+ Add other members (${extraMembers.length})`}
                  </button>
                )}
                {format === 'squad' ? (
                  <>
                    {is3v3 && (
                      <p className="rounded-xl bg-primary/10 border border-primary/30 px-3 py-2 text-xs font-semibold text-primary" data-testid="squad-3v3-hint">
                        3v3 tie — best of 3. Each squad's three pairs take the court once: AB v DE, BC v EF, AC v DF.
                      </p>
                    )}
                    <SquadEditor
                      squads={squads}
                      ratingOf={ratingOf}
                      canEdit={data.can_score}
                      onChange={setSquads}
                      onReSuggest={() => setSquads(suggestSquads(selected, ratingOf))}
                    />
                    <AvoidPairsEditor
                      groupId={group?.id}
                      pairs={data.avoid_pairs || []}
                      players={[...new Set([...squads[0], ...squads[1], ...data.candidates.map((c) => c.id)])].filter((id) => !String(id).startsWith('guest_'))}
                      nameOf={(id) => ratingOf[id]?.name || 'Guest'}
                      canEdit={data.can_score}
                      onChanged={(pairs) => setData((d) => ({ ...d, avoid_pairs: pairs }))}
                    />
                    <div className="rounded-2xl border p-3 flex items-start justify-between gap-3" data-testid="squad-no-repeat-row">
                      <div>
                        <p className="text-xs font-semibold">Never repeat a pair</p>
                        <p className="text-xs text-muted-foreground">
                          Inside each squad everyone gets a fresh partner every round, as far as the squad size allows.
                        </p>
                      </div>
                      <Switch checked={noRepeat} onCheckedChange={setNoRepeat} disabled={!data.can_score} data-testid="squad-no-repeat-switch" />
                    </div>
                  </>
                ) : PAIR_FORMATS.includes(format) ? (
                  <PairsEditor
                    pairs={pairs}
                    unpaired={unpaired}
                    ratingOf={ratingOf}
                    canEdit={data.can_score}
                    picking={picking}
                    onPick={pickForPair}
                    onChange={setPairs}
                    onReSuggest={() => { setPicking(null); setPairs(suggestPairs(selected, ratingOf)); }}
                  />
                ) : (
                  <>
                    <AvoidPairsEditor
                      groupId={group?.id}
                      pairs={data.avoid_pairs || []}
                      players={[...new Set([...selected, ...data.candidates.map((c) => c.id)])].filter((id) => !String(id).startsWith('guest_'))}
                      nameOf={(id) => ratingOf[id]?.name || 'Guest'}
                      canEdit={data.can_score}
                      onChanged={(pairs) => setData((d) => ({ ...d, avoid_pairs: pairs }))}
                    />
                    <div className="rounded-2xl border p-3 flex items-start justify-between gap-3" data-testid="no-repeat-row">
                      <div>
                        <p className="text-xs font-semibold">Never repeat a pair</p>
                        <p className="text-xs text-muted-foreground">
                          Everyone gets a brand-new partner every round. Off = repeats are just discouraged.
                        </p>
                      </div>
                      <Switch checked={noRepeat} onCheckedChange={setNoRepeat} disabled={!data.can_score} data-testid="no-repeat-switch" />
                    </div>
                  </>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Stepper label="Courts" value={courts} min={1} max={8} onChange={setCourts} testId="courts" />
                  {format === 'pairs' ? (
                    <div className="rounded-xl border p-2.5" data-testid="pairs-rounds-note">
                      <p className="text-xs font-semibold">Rounds</p>
                      <p className="text-xs text-muted-foreground">
                        Auto — {Math.max(0, (pairs.length * (pairs.length - 1)) / 2)} matches
                        {pairs.length >= 2 ? ` over ${Math.ceil(((pairs.length * (pairs.length - 1)) / 2) / courts)} rounds` : ''}
                      </p>
                    </div>
                  ) : PROGRESSIVE.includes(format) ? (
                    <div className="rounded-xl border p-2.5" data-testid="progressive-rounds-note">
                      <p className="text-xs font-semibold">Rounds</p>
                      <p className="text-xs text-muted-foreground">
                        One at a time — the next is built from the results
                      </p>
                    </div>
                  ) : (
                    <Stepper label="Rounds" value={rounds} min={1} max={12} onChange={setRounds} testId="rounds"
                      hint={`≈ ${data.settings.match_minutes} min each`} />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Stepper label="Points" value={pointsToWin} min={5} max={32} onChange={setPointsToWin} testId="points"
                    hint="per game" />
                  {format === 'americano' ? (
                    <Stepper label="Bonus" value={winBonus} min={0} max={10} onChange={setWinBonus} testId="bonus"
                      hint="per win" />
                  ) : <div />}
                </div>
                {format === 'groups_knockout' && (
                  <div className="grid grid-cols-2 gap-2">
                    <Stepper label="Groups" value={numGroups} min={2} max={8} onChange={setNumGroups} testId="groups"
                      hint={`${pairs.length} pairs`} />
                    <Stepper label="Go through" value={advance} min={1} max={4} onChange={setAdvance} testId="advance"
                      hint="per group" />
                  </div>
                )}
                <div className="rounded-2xl border p-3 flex items-center justify-between gap-3" data-testid="format-fee-row">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold">Fee for this session</p>
                    <p className="text-xs text-muted-foreground">
                      Charge a tournament day differently. Blank keeps the group default.
                    </p>
                  </div>
                  <input type="number" inputMode="numeric" min="0" value={fee} placeholder="—"
                    onChange={(e) => setFee(e.target.value)} disabled={!data.can_score}
                    data-testid="format-fee-input"
                    className="h-10 w-20 shrink-0 rounded-xl border bg-background text-center font-display font-bold tabular-nums" />
                </div>
                <div className="rounded-2xl border p-3 flex items-start justify-between gap-3" data-testid="rated-row">
                  <div>
                    <p className="text-xs font-semibold">Count toward ratings</p>
                    <p className="text-xs text-muted-foreground">
                      Off = a friendly. Scores still decide today's standings, but nobody's Elo moves.
                    </p>
                  </div>
                  <Switch checked={rated} onCheckedChange={setRated} disabled={!data.can_score} data-testid="rated-switch" />
                </div>
                <p className="text-xs text-muted-foreground">
                  {selected.length} players · {Math.min(courts * 4, Math.floor(selected.length / 4) * 4)} on court per round · {Math.max(0, selected.length - Math.min(courts * 4, Math.floor(selected.length / 4) * 4))} sitting out
                </p>
                {!data.can_score && <p className="text-xs text-amber-700 dark:text-amber-300">You're viewing only — ask an admin to make you a scorer to shuffle or log.</p>}
                <Button className="rounded-full w-full h-11" onClick={shuffle} disabled={busy || !data.can_score || selected.length < 4} data-testid="shuffle-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Shuffle className="w-4 h-4 mr-1.5" /> {{
                    squad: 'Build squad tie',
                    pairs: 'Build round robin',
                    americano: 'Build Americano',
                    mexicano: 'Start Mexicano',
                    king: 'Start King of the Court',
                    knockout: 'Draw the bracket',
                    groups_knockout: 'Draw the groups',
                  }[format] || (data.plan ? 'Re-shuffle teams' : 'Shuffle balanced teams')}</>}
                </Button>
              </div>
            )}

            {view === 'plan' && (
              data.plan?.rounds?.length ? (
                <PlanView data={data} ratingOf={ratingOf} sessionId={sessionId} onLogged={onLogged} onReshuffle={() => setView('players')} />
              ) : (
                <div className="text-center py-8 text-sm text-muted-foreground" data-testid="plan-empty">
                  No schedule yet. <button className="text-primary font-semibold" onClick={() => setView('players')}>Shuffle teams</button> first.
                </div>
              )
            )}

            {view === 'quick' && (
              <QuickMatchForm data={data} sessionId={sessionId} players={selected.length ? selected : data.candidates.map((c) => c.id)} ratingOf={ratingOf} onLogged={onLogged} />
            )}

            {data.matches.length > 0 && (
              <div className="rounded-2xl border px-3 pt-2 pb-1" data-testid="session-matches">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="font-display text-xs font-bold uppercase tracking-[0.16em]">Logged this session ({data.matches.length})</p>
                  <CardActions
                    compact
                    buildPath={`/sessions/${sessionId}/play/cards/results`}
                    sendPath={`/sessions/${sessionId}/play/cards/results/send`}
                    canSend={data.can_score}
                    filename={`results-${data.session.date}.png`}
                    shareText={`${data.session.title} · results`}
                    pdf={{
                      buildPath: `/sessions/${sessionId}/play/cards/results-pdf`,
                      sendPath: `/sessions/${sessionId}/play/cards/results-pdf/send`,
                      filename: `results-${data.session.date}.pdf`,
                    }}
                  />
                </div>
                <div className="divide-y divide-border/60">
                  {[...data.matches].reverse().map((m) => (
                    <MatchLine key={m.id} m={m} userId={user?.id} compact
                      canDelete={(group?.admin_ids || []).includes(user?.id) || (data.can_score && m.logged_by === user?.id)}
                      onDeleted={load} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function PlayerChip({ p, on, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={`player-chip-${p.id}`}
      className={`inline-flex items-center gap-1.5 pl-1 pr-3 h-9 rounded-full border text-xs font-semibold transition-colors ${on ? 'bg-amber-500/15 border-amber-500/60' : 'bg-background border-border text-muted-foreground'}`}>
      <Avatar name={p.name} size="xs" /> {firstName(p.name)}
      <span className="text-xs tabular-nums opacity-70">{p.rated === false ? 'guest' : p.rating}</span>
    </button>
  );
}

function Stepper({ label, value, min, max, onChange, testId, hint }) {
  return (
    <div className="rounded-xl border p-2.5 flex items-center justify-between gap-1" data-testid={`stepper-${testId}`}>
      <div className="min-w-0">
        <p className="text-xs font-semibold truncate">{label}</p>
        {hint && <p className="text-xs text-muted-foreground truncate">{hint}</p>}
      </div>
      <div className="inline-flex items-center gap-1 shrink-0">
        <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => onChange(Math.max(min, value - 1))} data-testid={`stepper-${testId}-minus`}><Minus className="w-3.5 h-3.5" /></Button>
        <span className="w-6 text-center font-display font-black tabular-nums" data-testid={`stepper-${testId}-value`}>{value}</span>
        <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => onChange(Math.min(max, value + 1))} data-testid={`stepper-${testId}-plus`}><Plus className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
}
